import { observer } from 'mobx-react-lite'
import {
  FirmDashboardOverviewSidebar,
  FirmDashboardOverviewSidebarHeader,
  FirmDashboardOverviewSidebarJob,
  FirmDashboardOverviewSidebarJobList,
} from '@/features/jobs/dashboard/components/FirmDashboardOverviewSidebar'
import { type FirmDashboardOverviewSidebarViewModel } from '@/features/jobs/dashboard/FirmDashboardOverviewSidebarViewModel'
import { InfiniteScrollLoaderContainer } from '@/components/InfiniteScrollLoader/InfiniteScrollLoaderContainer'
import { type SearchableJob } from '@/features/jobs/list/SearchableJob'
import { links } from '@/utils/link'
import { match } from '@/utils/pattern-match-util'

export interface FirmDashboardOverviewSidebarContainerProps {
  vm: FirmDashboardOverviewSidebarViewModel
}

export const FirmDashboardOverviewSidebarContainer = observer(
  function FirmDashboardOverviewSidebarContainer({
    vm,
  }: FirmDashboardOverviewSidebarContainerProps) {
    return (
      <FirmDashboardOverviewSidebar>
        <OverviewSidebarHeaderContainer vm={vm} />
        <OverviewSidebarJobListContainer vm={vm} />
      </FirmDashboardOverviewSidebar>
    )
  },
)

const OverviewSidebarHeaderContainer = observer(
  function OverviewSidebarHeaderContainer({
    vm,
  }: FirmDashboardOverviewSidebarContainerProps) {
    return (
      <FirmDashboardOverviewSidebarHeader
        greeting={match(vm.phaseOfDay, {
          Morning: () => `Good morning, ${vm.userName}!`,
          Afternoon: () => `Good afternoon, ${vm.userName}!`,
          Evening: () => `Good evening, ${vm.userName}!`,
        })}
        submittedJobsTotalPrice={vm.jobsTotalPricing.submittedJobsTotalPrice}
        closedJobsTotalPrice={vm.jobsTotalPricing.closedJobsTotalPrice}
        canViewContractAmount={vm.canViewContractAmount}
        contractUsage={vm.contractUsage}
        contractTotal={vm.contractVm.contractTotal}
        setupFees={vm.contractVm.setupFees}
        vmFees={vm.contractVm.vmFees}
        vmFeesQuantity={vm.contractVm.vmFeesQuantity}
        vmFeesAmountPerUnit={vm.contractVm.vmFeesAmountPerUnit}
        isContractConfigEnabled={vm.contractVm.isContractConfigEnabled}
        contractStartDate={vm.contractVm.contractStartDate}
        contractEndDate={vm.contractVm.contractEndDate}
      />
    )
  },
)

const OverviewSidebarJobListContainer = observer(
  function OverviewSidebarJobListContainer({
    vm,
  }: FirmDashboardOverviewSidebarContainerProps) {
    return (
      <FirmDashboardOverviewSidebarJobList
        openItemsJobsCount={vm.openItemsJobsCount}
        draftReviewJobsCount={vm.draftReviewJobsCount}
        openItemsJobs={vm.openItemsJobs.map((job) => (
          <OverviewSidebarJobContainer key={job.id} vm={vm} job={job} />
        ))}
        draftInReviewJobs={vm.draftInReviewJobs.map((job) => (
          <OverviewSidebarJobContainer key={job.id} vm={vm} job={job} />
        ))}
        scrollLoader={
          <InfiniteScrollLoaderContainer
            compact
            scrollProvider={vm.searcher.scroller}
          />
        }
      />
    )
  },
)

interface OverviewSidebarJobContainerProps {
  vm: FirmDashboardOverviewSidebarViewModel
  job: SearchableJob
}

const OverviewSidebarJobContainer = observer(
  function OverviewSidebarJobContainer({
    vm,
    job,
  }: OverviewSidebarJobContainerProps) {
    return (
      <FirmDashboardOverviewSidebarJob
        to={links.job(job.id)}
        displayName={job.displayName}
        lastUpdate={vm.lastUpdateOf(job)}
        avatarSubjects={vm.avatarMembersOf(job).map((member) => ({
          avatar: member.avatar,
          avatarFallback: member.avatarFallback,
          displayName: member.displayName,
        }))}
      />
    )
  },
)
